import React, {useEffect, useRef, useState} from 'react';
import axios from 'axios';

import './App.css';

import {useGoogleReCaptcha} from 'react-google-recaptcha-v3';
import {SelectButton} from "primereact/selectbutton";
import {InputText} from "primereact/inputtext";
import {Dropdown} from "primereact/dropdown";
import {FileUpload, FileUploadFilesParam} from "primereact/fileupload";
import {useFormik} from 'formik';
import {classNames} from "primereact/utils";
import * as Yup from 'yup';
import {Button} from "primereact/button";
import {countryNames} from 'country-region-data';
import {InputTextarea} from "primereact/inputtextarea";
import {Card} from "primereact/card";
import {Toast} from 'primereact/toast';
import {ProgressBar} from "primereact/progressbar";

export interface Submission {
    oxaAbGene?: boolean;
    kaptive?: boolean;
    firstName?: string;
    lastName?: string;
    country?: string;
    email?: string;
    institution?: string;
    repository?: string;
    otherRepository?: string;
    accessionNumber?: string;
    strainName?: string;
    additionalInformation?: string;
    fileName?: string;
}

const development: boolean = !process.env.NODE_ENV || process.env.NODE_ENV === 'development';
const apiHost: string = development ? 'http://localhost:3001' : '';

function App() {

    const [id, setId] = useState<string | null>(null);
    const {executeRecaptcha} = useGoogleReCaptcha();
    const toast = useRef<Toast>(null);
    const fileUpload = useRef<FileUpload>(null);

    useEffect(() => {
        if (id) {
            return;
        }
        const startSubmission = async () => {
            axios.get(`${apiHost}/submission`)
                .then(response => setId(response.data))
                .catch((error) => toast.current?.show({
                    severity: 'error',
                    summary: 'Error',
                    detail: error.message,
                    sticky: true
                }));
        };
        startSubmission().then();
    }, [id]);

    const repositoryOptions = [
        {name: 'NCBI', value: 'NCBI'},
        {name: 'ENA', value: 'ENA'},
        {name: 'Other', value: 'OTHER'}
    ];

    const yesNoOption = [
        {name: 'Yes', value: true},
        {name: 'No', value: false}
    ];

    const submissionForm = useFormik<Submission>({
        initialValues: {
            oxaAbGene: undefined,
            kaptive: undefined,
            firstName: '',
            lastName: '',
            country: '',
            email: '',
            institution: '',
            repository: '',
            otherRepository: '',
            accessionNumber: '',
            strainName: '',
            additionalInformation: '',
            fileName: ''
        },
        validateOnBlur: true,
        validationSchema: Yup.object().shape({
            firstName: Yup.string()
                .min(2, 'Minimum length of first name is 2 characters')
                .max(255, 'Maximum length of first name is 255 characters')
                .required('First name is required'),
            lastName: Yup.string()
                .min(2, 'Minimum length of last name is 2 characters')
                .max(255, 'Maximum length of last name is 255 characters')
                .required('Last name is required'),
            institution: Yup.string()
                .min(2, 'Minimum length of institution is 2 characters')
                .max(255, 'Maximum length of institution is 255 characters')
                .required('Institution is required'),
            country: Yup.string()
                .required('Country is required'),
            email: Yup.string().email('Must be a valid email address').required('Required'),
            repository: Yup.string()
                .oneOf(repositoryOptions.map((repositoryOption) => repositoryOption.value))
                .required('Repository is required'),
            otherRepository: Yup.string()
                .max(1000, 'Maximum length of other repository is 1000 characters')
                .when("repository", {
                    is: "OTHER",
                    then: Yup.string().required("Other repository is required")
                }),
            accessionNumber: Yup.string()
                .min(5, 'Minimum length of accession number is 5 characters')
                .max(255, 'Maximum length of accession number is 255 characters')
                .required('Accession number is required'),
            strainName: Yup.string()
                .min(1, 'Minimum length of strain name is 1 characters')
                .max(255, 'Maximum length of strain name is 255 characters')
                .required('Strain name is required'),
            additionalInformation: Yup.string()
                .max(400, 'Additional information max length is 4000 characters'),
            fileName: Yup.string()
                .max(1000, 'Genome-assembly file name max length is 1000 characters')
                .required('Genome-assembly file is required'),

        }),
        onSubmit: (submission) => {

            executeRecaptcha && executeRecaptcha('submit')
                .then((token) => axios
                    .post(`${apiHost}/submission`, {id, ...submission}, {headers: {token}})
                    .catch((error) => {
                        if (error.response) {
                            // The request was made and the server responded with a status code
                            // that falls out of the range of 2xx
                            console.log(error.response.status, error.response.data, error.response.headers);
                        } else if (error.request) {
                            // The request was made but no response was received
                            // `error.request` is an instance of XMLHttpRequest in the browser and an instance of
                            // http.ClientRequest in node.js
                            console.log(error.request);
                        } else {
                            // Something happened in setting up the request that triggered an Error
                            console.log('Error', error.message);
                        }
                        console.log(error.config);
                        toast.current?.show({severity: 'error', summary: 'Error', detail: error.message, sticky: true});
                        submissionForm.setSubmitting(false);
                    }));
        }
    });

    const submitAgain = (event: any) => {
        console.log(submissionForm.values)
        const {firstName, lastName, country, email, institution} = {...submissionForm.values};
        axios.get(`${apiHost}/submission`)
            .then(response => setId(response.data))
            .then(() => submissionForm.resetForm({
                values: {
                    firstName, lastName, country, email, institution,
                    repository: '',
                    otherRepository: '',
                    accessionNumber: '',
                    strainName: '',
                    additionalInformation: '',
                    fileName: ''
                }
            }))
            .catch((error) => toast.current?.show({
                severity: 'error',
                summary: 'Error',
                detail: error.message,
                sticky: true
            }));
    }

    const reset = (event: any) => submissionForm.resetForm();


    const isValid = (fieldName: keyof Submission) => !!(submissionForm.touched[fieldName] && submissionForm.errors[fieldName]);
    const getErrorMessage = (fieldName: keyof Submission) => {
        return isValid(fieldName) && <small className="p-error">{submissionForm.errors[fieldName]}</small>;
    };

    const fileType = (file: File): string => file.type || (file.name.endsWith('.gz') ? 'application/gzip' : 'text/plain');
    const onUpload = (event: FileUploadFilesParam) => {
        event.files.forEach((file) =>
            axios.post(`${apiHost}/presign`,
                {
                    id,
                    fileName: file.name,
                    fileType: fileType(file)
                })
                .then(response => response.data as string)
                .then((presignedUrl) =>
                    axios.put(
                        presignedUrl,
                        file,
                        {
                            headers: {
                                'Content-Type': fileType(file),
                            }
                        }
                    ))
                .then(() => {
                    submissionForm.setFieldValue('fileName', file.name, true)
                    fileUpload.current?.clear();
                })
                .catch((error) => toast.current?.show({
                    severity: 'error',
                    summary: 'Error',
                    detail: error.message,
                    sticky: true
                }))
        );
    }

    const onUploadRemove = () => {
        axios.delete(`${apiHost}/presign/${id}`)
            .then(response => response.data as string)
            .then((presignedUrl) =>
                axios.delete(
                    presignedUrl
                ))
            .then((response) => {
                submissionForm.setFieldValue('fileName', '', true)
            })
            .catch((error) => toast.current?.show({
                severity: 'error',
                summary: 'Error',
                detail: error.message,
                sticky: true
            }));
    }


    const emptyTemplate = () => submissionForm.values.fileName
        ? <div className="p-fileupload-row" key={submissionForm.values.fileName}>
            <div>{submissionForm.values.fileName}</div>
            <div><Button type="button" icon="pi pi-times" onClick={onUploadRemove} className={'p-button-secondary'}
                         label={"Clear Upload"}/></div>
        </div>
        :
        <p className="m-0">Genome-assembly file: (.fasta, .fasta.gz, .fna, .fna.gz). Drag and drop a file here or click
            the choose button to upload.</p>

    const progressBarTemplate = () => <ProgressBar mode={"indeterminate"}/>

    if (submissionForm.isSubmitting) {
        return <Card>
            <main>
                <h2>Submission Successful</h2>
                <p>Thank you for your submission to the <i>Acinetobacter baumannii</i> K locus submission portal. We
                    will
                    endeavour to process your submission as soon as possible. </p>
                <p>If you have any questions or require assistance, please contact <a
                    href="mailto:bacterial.polysaccharides@qut.edu.au">bacterial.polysaccharides@qut.edu.au</a></p>
                <Button label="Make Another Submission" className="mt-2" onClick={submitAgain}/>
            </main>
        </Card>
    }


    return (


        <Card>
            <Toast ref={toast}/>
            <main>
                <section>
                    <h2>Making a submission</h2>
                    <p>Typing and annotation of the <i>Acinetobacter baumannii</i> K locus follows the FAIR principles
                        for long-term discovery and usability of data. To assign a new KL number, we require the whole
                        genome sequence of the strain to be readily accessible.</p>
                    <h3>This portal is for novel submissions only</h3>
                    <p>Please create one submission per strain for review.</p>
                </section>



                <section>
                    <h2>Before you make a submission</h2>
                    <p>Does the genome include the intrinsic <i>oxaAb</i> gene confirming
                        the <i>Acinetobacter baumannii</i> species designation?</p>
                    <SelectButton id={"oxaAbGene"}
                                  options={yesNoOption}
                                  value={submissionForm.values.oxaAbGene}
                                  onChange={submissionForm.handleChange}
                                  optionLabel="name"/>

                    {!submissionForm.values.oxaAbGene &&
                        <div className="p-message p-component p-message-info">
                            <div className="p-message-wrapper">
                    <span className="p-message-detail">
                        <p>You can confirm <i>A. baumannii</i> species designation of your genome via blastp search of <i>oxaAb</i> gene (also referred to as blaOXA-51; GenBank accession number <a
                            href="https://www.ncbi.nlm.nih.gov/nuccore/CP010781.1">CP010781.1</a>, base positions 1753305–1754129).</p>
                        <p>This step must be completed before lodging your submission.</p></span>
                            </div>
                        </div>
                    }

                    {submissionForm.values.oxaAbGene === true &&
                        <React.Fragment>
                            <p>Has the genome been previously submitted as a query to Kaptive-Web or the Kaptive source
                                code
                                for preliminary typing?</p>
                            <SelectButton id={"kaptive"} options={yesNoOption}
                                          value={submissionForm.values.kaptive}
                                          onChange={submissionForm.handleChange}
                                          optionLabel="name"/>
                        </React.Fragment>
                    }

                    {submissionForm.values.oxaAbGene === true && !submissionForm.values.kaptive &&

                        <div className="p-message p-component p-message-info">
                            <div className="p-message-wrapper">
                            <span className="p-message-detail">
                                <p>Please first determine that your sequence is novel by screening your genome assembly against the
                                current <i>A. baumannii</i> KL reference database available via the following links:

                                </p>
                                <ul>
                                    <li><a href={"https://kaptive-web.erc.monash.edu/"}>Kaptive-Web</a></li>
                                    <li><a href={"https://github.com/kelwyres/Kaptive-Web"}>Kaptive-Web Github Repo</a></li>
                                </ul>

                                <p>Genomes with a match confidence score less than 'Perfect' should be manually inspected to
                                    confirm that any novel sequence is not due to the presence of insertion sequences (IS) or issues
                                    with sequence/assembly quality.</p>
                                <p>This step must be completed before lodging your submission.</p>
                            </span>
                            </div>
                        </div>

                    }

                </section>


                {submissionForm.values.oxaAbGene === true && submissionForm.values.kaptive === true &&

                    <form onSubmit={submissionForm.handleSubmit}>

                        <section>
                            <h2>Submitter's Information</h2>
                            <p className={'mb-5'}>Submitter's details are recorded to provide a contact for the
                                uploaded sequence data and the submission for K locus typing.</p>


                            <div className="p-fluid ">
                                <div className="field mb-5">
                                    <span className="p-float-label">
                                        <InputText id="firstName" value={submissionForm.values.firstName}
                                                   onChange={submissionForm.handleChange}
                                                   onBlur={submissionForm.handleBlur}
                                                   className={classNames({'p-invalid': isValid('firstName')})}/>
                                        <label htmlFor="firstName"
                                               className={classNames({'p-error': isValid('firstName')})}>First Name *</label>
                                    </span>
                                    {getErrorMessage('firstName')}
                                </div>
                                <div className="field mb-5">
                                    <span className="p-float-label">
                                        <InputText id="lastName" value={submissionForm.values.lastName}
                                                   onChange={submissionForm.handleChange}
                                                   onBlur={submissionForm.handleBlur}
                                                   className={classNames({'p-invalid': isValid('lastName')})}/>
                                        <label htmlFor="lastName"
                                               className={classNames({'p-error': isValid('lastName')})}>Last Name *</label>
                                    </span>
                                    {getErrorMessage('lastName')}
                                </div>
                                <div className="field mb-5">
                                    <span className="p-float-label">
                                         <Dropdown id="country" value={submissionForm.values.country}
                                                   options={countryNames}
                                                   onChange={submissionForm.handleChange}
                                                   onBlur={submissionForm.handleBlur}
                                                   className={classNames({'p-invalid': isValid('country')})}/>
                                        <label htmlFor="country"
                                               className={classNames({'p-error': isValid('country')})}>Country *</label>

                                    </span>
                                    {getErrorMessage('country')}
                                </div>
                                <div className="field mb-5">
                                    <span className="p-float-label">
                                        <InputText id="institution" value={submissionForm.values.institution}
                                                   onChange={submissionForm.handleChange}
                                                   onBlur={submissionForm.handleBlur}
                                                   className={classNames({'p-invalid': isValid('institution')})}/>
                                        <label htmlFor="lastName"
                                               className={classNames({'p-error': isValid('institution')})}>Institution/Affiliation *</label>

                                    </span>
                                    {getErrorMessage('institution')}
                                </div>
                                <div className="field mb-5">
                                    <span className="p-float-label p-input-icon-right">
                                          <i className="pi pi-envelope"/>
                                        <InputText id="email" value={submissionForm.values.email}
                                                   onChange={submissionForm.handleChange}
                                                   onBlur={submissionForm.handleBlur}
                                                   className={classNames({'p-invalid': isValid('email')})}/>
                                        <label htmlFor="email"
                                               className={classNames({'p-error': isValid('email')})}>Email *</label>

                                    </span>
                                    {getErrorMessage('email')}
                                </div>
                            </div>
                        </section>


                        <section>
                            <h2 className={'mb-5'}>Submission</h2>
                            <div className="p-fluid ">
                                <div className="field mb-5">
                                <span className="p-float-label">
                                    <Dropdown id={"repository"} value={submissionForm.values.repository}
                                              options={repositoryOptions}
                                              optionLabel="name"
                                              onChange={submissionForm.handleChange}
                                              className={classNames({'p-invalid': isValid('firstName')})}/>
                                    <label htmlFor="repository"
                                           className={classNames({'p-error': isValid('repository')})}>Repository *</label>
                                </span>
                                    {getErrorMessage('repository')}
                                </div>


                                {submissionForm.values.repository === 'OTHER' &&
                                    <div className="field mb-5">
                                        <span className="p-float-label">
                                            <InputText id="otherRepository"
                                                       value={submissionForm.values.otherRepository}
                                                       onChange={submissionForm.handleChange}
                                                       onBlur={submissionForm.handleBlur}
                                                       className={classNames({'p-invalid': isValid('otherRepository')})}/>
                                            <label htmlFor="otherRepository"
                                                   className={classNames({'p-error': isValid('otherRepository')})}>Other Repository (should be publicly available) *</label>

                                        </span>
                                        {getErrorMessage('otherRepository')}
                                    </div>
                                }
                                <div className="field mb-5">
                                    <span className="p-float-label">
                                        <InputText id="accessionNumber"
                                                   value={submissionForm.values.accessionNumber}
                                                   onChange={submissionForm.handleChange}
                                                   onBlur={submissionForm.handleBlur}
                                                   className={classNames({'p-invalid': isValid('accessionNumber')})}/>
                                        <label htmlFor="accessionNumber"
                                               className={classNames({'p-error': isValid('accessionNumber')})}>Accession Number *</label>

                                    </span>
                                    {getErrorMessage('accessionNumber')}
                                </div>
                                <div className="field mb-5">
                                    <span className="p-float-label">
                                        <InputText id="strainName"
                                                   value={submissionForm.values.strainName}
                                                   onChange={submissionForm.handleChange}
                                                   onBlur={submissionForm.handleBlur}
                                                   className={classNames({'p-invalid': isValid('strainName')})}/>
                                        <label htmlFor="strainName"
                                               className={classNames({'p-error': isValid('strainName')})}>Strain Name *</label>

                                    </span>
                                    {getErrorMessage('strainName')}
                                </div>
                                <div className="field mb-5">
                                    <span className="p-float-label">
                                        <InputTextarea id="additionalInformation" maxLength={400}
                                                       value={submissionForm.values.additionalInformation}
                                                       onChange={submissionForm.handleChange}
                                                       onBlur={submissionForm.handleBlur}
                                                       className={classNames({'p-invalid': isValid('additionalInformation')})}/>
                                        <label htmlFor="additionalInformation"
                                               className={classNames({'p-error': isValid('additionalInformation')})}>Additional Information</label>

                                    </span>
                                    {getErrorMessage('additionalInformation')}
                                </div>
                            </div>

                            <FileUpload name="demo[]" onUpload={onUpload} ref={fileUpload}
                                        accept=".fasta,.fna,.gz"
                                        maxFileSize={10000000} multiple={false}
                                        auto customUpload uploadHandler={onUpload} onRemove={onUploadRemove}
                                        emptyTemplate={emptyTemplate}
                                        progressBarTemplate={progressBarTemplate}
                            />

                            {getErrorMessage('fileName')}
                        </section>

                        <Button type="submit" label="Submit" className="mt-2"/> <Button type="reset" label="Reset"
                                                                                        onClick={reset}
                                                                                        className="mt-2 ml-1 p-button-secondary"/>

                    </form>


                }
            </main>
        </Card>


    );
}

export default App;
